import * as React from "react";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import type {
    DuprProfileDto,
    DuprRatingDto,
    DuprSsoConfigDto,
} from "@volley/data";
import {
    formatDuprRating,
    formatDuprReliability,
    isDuprRated,
} from "@volley/data/predicted-rating";

import fetchApi, { FetchError, logFetchError } from "../../util/fetchApi";
import CloseableDialogTitle from "../common/CloseableDialogTitle";

interface RatingMessage {
    userToken?: string;
    refreshToken?: string;
    duprId?: string;
    id?: string | number;
    /** DUPR's stats blob — the link-time source of ratings. */
    stats?: unknown;
}

const GENERIC_LINK_ERROR = "Could not link your DUPR® account.";

/**
 * The link refusals a player can actually do something about. Copy lives here
 * rather than echoing the server's `message` so the ® styling matches the rest
 * of this component, and it is keyed off the machine-readable `reason` so an
 * unrecognized failure (or a 500 body) can never be rendered at a player.
 */
const LINK_ERRORS: Record<string, string> = {
    dupr_account_in_use:
        "That DUPR® account is already connected to a different Volley account.",
    // "Unlink", not "disconnect" — that is what the button in this component
    // is labelled, and it is on screen whenever this refusal can happen.
    different_account_linked:
        "This Volley account is already connected to a different DUPR® account. Unlink it before connecting a new one.",
};

function linkErrorMessage(e: unknown): string {
    if (!(e instanceof FetchError) || e.statusCode !== 409) {
        return GENERIC_LINK_ERROR;
    }
    const { reason } = (e.body ?? {}) as { reason?: unknown };
    if (typeof reason !== "string") return GENERIC_LINK_ERROR;
    return LINK_ERRORS[reason] ?? GENERIC_LINK_ERROR;
}

function RatingBlock({
    label,
    rating,
    primary = false,
}: {
    label: string;
    rating: DuprRatingDto;
    primary?: boolean;
}): React.JSX.Element {
    const subscoreEntries = Object.entries(rating.subscores ?? {});
    const value = formatDuprRating(rating.value, { placeholder: "NR" });
    const verified = formatDuprRating(rating.verified, { placeholder: "" });
    // Build the metadata line from whatever DUPR actually returned. The
    // verified rating is compared AFTER formatting: DUPR sends three decimals,
    // so two ratings that render identically here can still differ in a digit
    // the player never sees, and showing "3.75 · Verified 3.75" reads as a bug.
    // Reliability arrives from DUPR already scaled 0..100, so it is formatted
    // rather than rescaled. Suppressed for an unrated format, where DUPR's 0 is
    // the absence of a rating and not a real confidence.
    const reliability = isDuprRated(rating.value)
        ? formatDuprReliability(rating.reliability)
        : null;
    const meta = [
        verified && verified !== value ? `Verified ${verified}` : null,
        reliability !== null ? `Reliability ${reliability}` : null,
        rating.careerHigh
            ? `Career high ${formatDuprRating(rating.careerHigh)}`
            : null,
    ].filter(Boolean);
    return (
        <Box>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                    {label}
                </Typography>
                {primary && (
                    <Chip label="Primary" size="small" variant="outlined" />
                )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="h4" component="span">
                    {value}
                </Typography>
                {rating.aiAnnotated && (
                    <AutoAwesomeIcon
                        fontSize="small"
                        color="primary"
                        titleAccess="Volley AI-estimated rating"
                    />
                )}
                {rating.provisional && !rating.aiAnnotated && (
                    <Chip label="Provisional" size="small" color="warning" />
                )}
            </Stack>
            {meta.length > 0 && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                >
                    {meta.join(" · ")}
                </Typography>
            )}
            {subscoreEntries.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.5}>
                    {subscoreEntries.map(([key, subscore]) => (
                        <Chip
                            key={key}
                            label={`${key}: ${formatDuprRating(subscore)}`}
                            size="small"
                            variant="outlined"
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
}

function DuprRatingCard({
    profile,
}: {
    profile: DuprProfileDto;
}): React.JSX.Element {
    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Box>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        mb={0.5}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 900,
                                letterSpacing: 1.5,
                                color: "#001762",
                            }}
                        >
                            DUPR®
                        </Typography>
                        <Chip
                            label="Connected"
                            size="small"
                            color="success"
                            variant="outlined"
                        />
                    </Stack>
                    <Typography variant="h4">
                        {profile.name ?? "DUPR® Member"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        DUPR® ID {profile.duprId}
                        {profile.syncedAt
                            ? ` · synced ${new Date(profile.syncedAt).toLocaleString()}`
                            : ""}
                    </Typography>
                </Box>
                <Divider />
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={3}
                    divider={<Divider orientation="vertical" flexItem />}
                >
                    <RatingBlock
                        label="Doubles"
                        rating={profile.doubles}
                        primary={profile.defaultRating === "DOUBLES"}
                    />
                    <RatingBlock
                        label="Singles"
                        rating={profile.singles}
                        primary={profile.defaultRating === "SINGLES"}
                    />
                </Stack>
            </Stack>
        </Paper>
    );
}

export default function DuprConnect({
    ssoConfig,
    onProfileChange,
    renderConnect,
    description,
}: {
    ssoConfig: DuprSsoConfigDto;
    /**
     * Notified whenever the linked profile changes (initial load, link, refresh,
     * unlink). Lets a parent read the official rating — e.g. to pre-fill an
     * assessment. Optional and backward-compatible; existing callers pass nothing.
     */
    onProfileChange?: (profile: DuprProfileDto | null) => void;
    /**
     * Render a custom connect control for the unlinked state, wired to the real
     * SSO flow via the passed `onConnect`. When omitted, the default DUPR-branded
     * button + blurb is shown. Lets callers (e.g. the DUPR dashboard) supply
     * their own CTA while reusing this component's SSO dialog and link handling.
     */
    renderConnect?: (onConnect: () => void) => React.ReactNode;
    /**
     * Prompt copy shown above the default connect button (unlinked state, when
     * `renderConnect` is not used). Lets each context supply its own framing —
     * e.g. onboarding vs. assessment. Falls back to a generic blurb when omitted.
     */
    description?: React.ReactNode;
}): React.JSX.Element {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [loading, setLoading] = React.useState(true);
    const [profile, setProfile] = React.useState<DuprProfileDto | null>(null);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState("");

    // Surface every profile transition (load/link/refresh/unlink) to the parent.
    React.useEffect(() => {
        onProfileChange?.(profile);
    }, [profile, onProfileChange]);

    // Load any existing link on mount. A 404 simply means "not linked yet".
    React.useEffect(() => {
        fetchApi<DuprProfileDto>("/api/integrations/dupr/me")
            .then((p) => setProfile(p))
            .catch((e) => {
                if (!(e instanceof FetchError && e.statusCode === 404)) {
                    logFetchError(e);
                }
            })
            .finally(() => setLoading(false));
    }, []);

    const onConnect = React.useCallback(() => {
        setDialogOpen(true);
    }, []);

    // While the login dialog is open, listen for the token postMessage that
    // DUPR's embedded login emits on success. Origin is validated against the
    // configured DUPR host before we trust the payload.
    React.useEffect(() => {
        if (!dialogOpen) return undefined;
        const expectedOrigin = new URL(ssoConfig.ssoLoginUrl).origin;

        const handler = (event: MessageEvent) => {
            if (event.origin !== expectedOrigin) return;
            const data: RatingMessage =
                event.data && typeof event.data === "object"
                    ? (event.data as RatingMessage)
                    : (event as unknown as RatingMessage);
            const { userToken, refreshToken, duprId, id, stats } = data;
            if (!userToken || !refreshToken || !duprId) return;

            setBusy(true);
            fetchApi<DuprProfileDto>("/api/integrations/dupr/link", "POST", {
                userToken,
                refreshToken,
                duprId,
                providerInternalId: id != null ? String(id) : null,
                stats,
            })
                .then((linked) => {
                    setProfile(linked);
                    setDialogOpen(false);
                })
                .catch((e) => {
                    logFetchError(e);
                    setError(linkErrorMessage(e));
                    // A 409 is a standing refusal, not a hiccup: re-running SSO
                    // in the iframe will land in exactly the same place, so
                    // close it rather than inviting a retry behind the snackbar.
                    if (e instanceof FetchError && e.statusCode === 409) {
                        setDialogOpen(false);
                    }
                })
                .finally(() => setBusy(false));
        };

        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, [dialogOpen, ssoConfig]);

    const onRefresh = React.useCallback(() => {
        setBusy(true);
        fetchApi<DuprProfileDto>("/api/integrations/dupr/me?refresh=true")
            .then((p) => setProfile(p))
            .catch((e) => {
                logFetchError(e);
                setError("Could not refresh DUPR® data.");
            })
            .finally(() => setBusy(false));
    }, []);

    const onUnlink = React.useCallback(() => {
        setBusy(true);
        fetchApi("/api/integrations/dupr/me", "DELETE")
            .then(() => setProfile(null))
            .catch((e) => {
                logFetchError(e);
                setError("Could not unlink your DUPR® account.");
            })
            .finally(() => setBusy(false));
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    const ssoSrc = `${ssoConfig.ssoLoginUrl}/login-external-app/${ssoConfig.base64ClientKey}`;

    return (
        <>
            {profile ? (
                <Stack spacing={2}>
                    <DuprRatingCard profile={profile} />
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            color="info"
                            onClick={onRefresh}
                            disabled={busy}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={onUnlink}
                            disabled={busy}
                        >
                            Unlink
                        </Button>
                    </Stack>
                </Stack>
            ) : renderConnect ? (
                renderConnect(onConnect)
            ) : (
                <Stack spacing={1.5}>
                    <Typography variant="body2" color="text.secondary">
                        {description ??
                            "Connect your DUPR® account to see your official pickleball ratings here."}
                    </Typography>
                    <Box>
                        {/* SSO entry point — styled per DUPR brand guidelines */}
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={onConnect}
                            sx={{
                                backgroundColor: "#001762",
                                color: "#ffffff",
                                textTransform: "none",
                                borderRadius: "6px",
                                fontWeight: 600,
                                fontSize: "0.9375rem",
                                letterSpacing: "0.015em",
                                px: 2.5,
                                py: 1,
                                gap: 1.5,
                                "&:hover": {
                                    backgroundColor: "#0f4299",
                                },
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    fontWeight: 900,
                                    letterSpacing: "0.08em",
                                    borderRight:
                                        "1px solid rgba(255,255,255,0.3)",
                                    pr: 1.5,
                                }}
                            >
                                DUPR®
                            </Box>
                            Connect account
                        </Button>
                    </Box>
                </Stack>
            )}

            <Dialog
                open={dialogOpen}
                fullWidth
                maxWidth="sm"
                fullScreen={fullScreen}
            >
                <CloseableDialogTitle onClose={() => setDialogOpen(false)}>
                    Connect your DUPR® account
                </CloseableDialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        p: 0,
                        m: 0,
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {busy && (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(255,255,255,0.6)",
                                zIndex: 1,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                    {ssoSrc && (
                        <Box
                            component="iframe"
                            title="DUPR® Login"
                            src={ssoSrc}
                            allow="payment"
                            sx={{
                                width: "100%",
                                height: fullScreen ? "100%" : 600,
                                flex: fullScreen ? 1 : "none",
                                border: 0,
                                display: "block",
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Snackbar
                autoHideDuration={5000}
                open={!!error}
                onClose={() => setError("")}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="error">{error}</Alert>
            </Snackbar>
        </>
    );
}
